<script setup lang="ts">
    import { computed, watch, onMounted, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import FileUploader from "@Blobs/Components/FileUploader.vue"
    import { Refresh } from '@browser/Browser'
    import { generateName } from '@common/Common'
    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'

    import { useSpreadsheetImportMappingQueryV1 } from '@Quest/Portfolios/queries-vue-composables'
    import { useImportProjectPlansFromSpreadsheetCommandV1 } from '@Quest/Portfolios/commands-vue-composables'

    import type { ProjectPlansSpreadsheetImportMappingReadModel } from '@Quest/Portfolios/readmodels'

    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))

    // ======================
    // Query
    // ======================

    const {
        runSpreadsheetImportMappingQueryV1,
        modelSpreadsheetImportMappingQueryV1
    } = useSpreadsheetImportMappingQueryV1(workspaceId, tchProjectId)

    const rm = modelSpreadsheetImportMappingQueryV1 as Ref<ProjectPlansSpreadsheetImportMappingReadModel | null>
    const portfolioId = computed<string | null>(() => rm.value?.PortfolioId ?? null)

    const file = ref<File | null>(null)

    // ======================
    // Command
    // ======================

    const {
        invokeImportProjectPlansFromSpreadsheetCommandV1,
        succeedImportProjectPlansFromSpreadsheetCommandV1
    } = useImportProjectPlansFromSpreadsheetCommandV1(workspaceId, portfolioId)

    // ======================
    // Methods
    // ======================

    function loadMappings(blobId: string)
    {
        runSpreadsheetImportMappingQueryV1({ BlobId: blobId })
    }

    function cancelImport()
    {
        rm.value = null
    }

    function handleImportProjectPlansFromSpreadsheetCommand()
    {
        if (!rm.value) return

        invokeImportProjectPlansFromSpreadsheetCommandV1({
            BlobId: rm.value.BlobId,
            ImportedPlanName: generateName("Imported plan")
        })
    }

    // ======================
    // Watchers
    // ======================

    watch(succeedImportProjectPlansFromSpreadsheetCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            Refresh()
        }
    })

    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Import | TransparentChoice"
    })
</script>

<template>
    <div>

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />


        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Import</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <!--<button role="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="">Add projects</button>-->
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <p v-if="!rm">
                <FileUploader @selected="file=$event" @unselected="file=null" @uploaded="loadMappings" />
            </p>

            <div v-if="rm">

                <button class="btn btn-secondary mr-1" @click="handleImportProjectPlansFromSpreadsheetCommand">Import</button>
                <button class="btn btn-secondary" @click="cancelImport">Cancel</button>

                <table class="table table-bordered">
                    <thead>
                        <tr>
                            <th>Sheet index</th>
                            <th>Sheet name</th>
                            <th>Project name</th>
                            <th>Timeline</th>
                            <th>Capex</th>
                        </tr>
                    </thead>
                    <tr v-for="s in rm.Sheets" :key="s.SheetIndex">
                        <td>{{s.SheetIndex}}</td>
                        <td>{{s.SheetName}}</td>
                        <td :class="{'table-success': s.ProjectExists, 'table-danger': !s.ProjectExists}">{{s.ProjectName}}</td>
                        <td :class="{'table-success': s.Timeline, 'table-danger': !s.Timeline}"></td>
                        <td :class="{'table-success': s.Capex, 'table-danger': !s.Capex}"></td>
                    </tr>
                </table>

            </div>
        </div>
    </div>
</template>